import React, { useState, useEffect, useRef } from "react";
import { supplyChainService } from "../services/apiClient";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { SupplierOrderRow } from "../types/api";

// Module-level cache to persist data across navigation (survives component unmount/remount)
const shippingAnalyticsCache: {
  data: Map<string, { value: any; timestamp: number }>;
  ttl: number;
} = {
  data: new Map(),
  ttl: 20 * 60 * 1000, // 20 minutes TTL
};

function getCachedData(key: string): any | null {
  const cached = shippingAnalyticsCache.data.get(key);
  if (cached && Date.now() - cached.timestamp < shippingAnalyticsCache.ttl) {
    console.log(` ShippingAnalytics: Using cached data for ${key}`);
    return cached.value;
  }
  return null;
}

function setCachedData(key: string, value: any): void {
  shippingAnalyticsCache.data.set(key, { value, timestamp: Date.now() });
  console.log(` ShippingAnalytics: Cached data for ${key}`);
}

// Types for shipping analytics data
interface ShippingOverview {
  total_shipments: number;
  total_cost: number;
  total_items: number;
  avg_cost_per_shipment: number;
  avg_items_per_shipment: number;
  countries_served: number;
  first_shipment_date: string | null;
  last_shipment_date: string | null;
}

interface ShippingByCountry {
  period: string;
  country: string;
  total_cost: number;
  avg_daily_cost: number;
  min_daily_cost: number;
  max_daily_cost: number;
  days_with_data: number;
}

interface BandData {
  country: string;
  band: string;
  shipment_count: number;
  avg_value: number;
  min_value: number;
  max_value: number;
}

interface CostPerItemData {
  country: string;
  shipment_count: number;
  total_cost: number;
  total_items: number;
  avg_cost_per_item: number;
  min_cost_per_item: number;
  max_cost_per_item: number;
}

interface TrendData {
  period: string;
  value: number;
  metric: string;
  country?: string;
}

//  Warehouse Analytics Types
interface WarehouseData {
  warehouse: string;
  order_count: number;
  total_volume: number | null;
  avg_volume_per_order: number | null;
  avg_weight_kg: number | null;
  avg_cost: number;
  total_cost: number;
  min_cost: number;
  max_cost: number;
  avg_duration_days: number | null;
}

interface OrderCompositionData {
  composition: string;
  order_count: number;
  percentage: number;
  avg_units_per_order: number;
  avg_cost: number;
  total_units_sold: number;
}

interface CnWarehouseByCountryData {
  destination_country: string;
  order_count: number;
  total_volume: number | null;
  avg_volume_per_order: number | null;
  avg_weight_kg: number | null;
  avg_cost: number;
  total_cost: number;
  avg_duration_days: number | null;
}

// Avg Cost By Country Over Time (Client Request)
interface AvgCostOverTimeData {
  period: string;
  country: string;
  order_count: number;
  avg_cost: number;
  total_cost: number;
  total_volume: number | null;
  min_cost: number;
  max_cost: number;
  total_cogs: number;
  avg_cogs: number;
}

// Supplier Order History Types
// order_number can arrive as a number and parsed_date as null, so the wire type
// from the API module is used directly rather than a stricter local copy.
type SupplierOrderData = SupplierOrderRow;

interface SupplierOrderSummary {
  total_orders: number;
  total_amount: number;
  total_quantity: number;
  by_status: {
    ordered: number;
    in_transit: number;
    delivered: number;
  };
}

interface SupplierFallbackInfo {
  active: boolean;
  rangeLabel: string;
}

// Custom Dropdown Component that always opens downward
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  label: string;
  isSearchVisible?: boolean;
  hideLabel?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  label,
  isSearchVisible = true,
  hideLabel = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayValue = value || placeholder;

  return (
    <div ref={dropdownRef} className="relative">
      {!hideLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-10 px-3 py-2 border rounded-md shadow-sm focus:outline-none text-sm bg-white text-left flex items-center justify-between transition-all ${isOpen
          ? 'border-blue-500 ring-2 ring-blue-200'
          : 'border-gray-300 hover:border-gray-400'
          }`}
      >
        <span className={value ? "text-gray-900 font-medium" : "text-gray-500"}>{displayValue}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl animate-fadeIn">
          {/* Search input */}
          {isSearchVisible && <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search regions..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>}

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto py-1">
            {placeholder !== "Select..." && <div
              className={`px-3 py-2.5 hover:bg-blue-50 cursor-pointer text-sm transition-colors flex items-center justify-between ${!value ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                }`}
              onClick={() => {
                onChange("");
                setIsOpen(false);
                setSearchTerm("");
              }}
            >
              <span>{placeholder}</span>
              {!value && (
                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>}

            {filteredOptions.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-gray-500">
                No regions found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option}
                  className={`px-3 py-2.5 hover:bg-blue-50 cursor-pointer text-sm transition-colors flex items-center justify-between ${value === option ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700 hover:text-gray-900'
                    }`}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <span>{option}</span>
                  {value === option && (
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ShippingAnalytics: React.FC = () => {
  // State management
  const [activeTab, setActiveTab] = useState<string>("by-warehouse");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Date filters
  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [groupBy, setGroupBy] = useState<string>("week");
  const [metric, setMetric] = useState<string>("cost");

  // Data state
  // const [overview, setOverview] = useState<ShippingOverview | null>(null);
  const [byCountryData, setByCountryData] = useState<ShippingByCountry[]>([]);
  const [costBands, setCostBands] = useState<BandData[]>([]);
  const [weightBands, setWeightBands] = useState<BandData[]>([]);
  const [costPerItem, setCostPerItem] = useState<CostPerItemData[]>([]);
  // const [trends, setTrends] = useState<TrendData[]>([]);
  const [countries, setCountries] = useState<string[]>([]);

  // Warehouse Analytics state
  const [warehouseAnalytics, setWarehouseAnalytics] = useState<WarehouseData[]>([]);
  const [orderComposition, setOrderComposition] = useState<OrderCompositionData[]>([]);
  const [cnWarehouseByCountry, setCnWarehouseByCountry] = useState<CnWarehouseByCountryData[]>([]);

  // Avg Cost Over Time state (Client Request)
  const [avgCostOverTime, setAvgCostOverTime] = useState<AvgCostOverTimeData[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");

  // Supplier Order History state
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrderData[]>([]);
  const [supplierOrderSummary, setSupplierOrderSummary] = useState<SupplierOrderSummary | null>(null);
  const [itemFilter, setItemFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [supplierFallbackInfo, setSupplierFallbackInfo] = useState<SupplierFallbackInfo>({
    active: false,
    rangeLabel: ""
  });

  // Origin filter state (for filtering by fulfillment provider)
  const [selectedOrigin, setSelectedOrigin] = useState<string>("");

  // Warehouse drill-down state
  const [expandedWarehouse, setExpandedWarehouse] = useState<string | null>(null);
  const [warehouseDrilldownData, setWarehouseDrilldownData] = useState<AvgCostOverTimeData[]>([]);
  const [loadingDrilldown, setLoadingDrilldown] = useState<boolean>(false);

  // Fetch countries on mount (with caching)
  useEffect(() => {
    const countryCacheKey = `countries-${selectedOrigin || "all"}`;
    const cachedCountries = getCachedData(countryCacheKey);
    if (cachedCountries) {
      setCountries(cachedCountries);
    } else {
      fetchCountries();
    }
  }, [selectedOrigin]);

  // Fetch data based on active tab (with caching)
  useEffect(() => {
    // Generate cache key based on current state
    const cacheKey = `${activeTab}-${startDate}-${endDate}-${selectedCountry}-${groupBy}-${metric}-${selectedOrigin}`;

    if (activeTab === "by-warehouse") {
      const warehouseCache = getCachedData(`${cacheKey}-warehouse`);
      const avgCostCache = getCachedData(`${cacheKey}-avg-cost`);
      if (warehouseCache) {
        setWarehouseAnalytics(warehouseCache);
      }
      if (avgCostCache) {
        setAvgCostOverTime(avgCostCache);
      }
      if (warehouseCache && avgCostCache) {
        setLoading(false);
        return;
      }
    }

    // Check if we have cached data for this exact query
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      // Use cached data - no API call needed
      applyCachedData(activeTab, cachedData);
      setLoading(false);
      return;
    }

    // No cache - fetch from API
    fetchData();
  }, [activeTab, startDate, endDate, selectedCountry, groupBy, metric, selectedOrigin]);

  //  Helper to apply cached data based on tab
  const applyCachedData = (tab: string, data: any) => {
    switch (tab) {
      // case "overview":
      //   setOverview(data);
      //   break;
      // case "by-country":
      //   setByCountryData(data);
      //   break;
      // case "trends":
      //   setTrends(data);
      //   break;
      case "by-warehouse":
        setWarehouseAnalytics(data);
        break;
      case "order-composition":
        setOrderComposition(data);
        break;
      // case "cn-warehouse":
      //   setCnWarehouseByCountry(data);
      //   break;
      // case "cost-over-time":
      //   setAvgCostOverTime(data);
      //   break;
      case "supplier-orders":
        if (data.orders) setSupplierOrders(data.orders);
        if (data.summary) setSupplierOrderSummary(data.summary);
        if (data.fallback) {
          setSupplierFallbackInfo(data.fallback);
        } else {
          setSupplierFallbackInfo({ active: false, rangeLabel: "" });
        }
        break;
      default:
        break;
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await supplyChainService.getShippingCountries(
        selectedOrigin || undefined
      );
      if (response?.data && Array.isArray(response.data)) {
        setCountries(response.data);
        setCachedData(`countries-${selectedOrigin || "all"}`, response.data); // Cache it
      }
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    // Generate cache key for storing results
    const cacheKey = `${activeTab}-${startDate}-${endDate}-${selectedCountry}-${groupBy}-${metric}-${selectedOrigin}`;

    try {
      switch (activeTab) {
        // case "overview":
        //   await fetchOverview(cacheKey);
        //   break;
        // case "by-country":
        //   await fetchByCountry(cacheKey);
        //   break;
        case "cost-bands":
          await fetchCostBands();
          break;
        case "weight-bands":
          await fetchWeightBands();
          break;
        case "cost-per-item":
          await fetchCostPerItem();
          break;
        // case "trends":
        //   await fetchTrends(cacheKey);
        //   break;
        case "by-warehouse":
          await fetchWarehouseAnalytics(`${cacheKey}-warehouse`);
          await fetchAvgCostOverTime(`${cacheKey}-avg-cost`); // Also fetch cost over time data for the chart
          break;
        case "order-composition":
          await fetchOrderComposition(cacheKey);
          break;
        // case "cn-warehouse":
        //   await fetchCnWarehouseByCountry(cacheKey);
        //   break;
        // case "cost-over-time":
        //   await fetchAvgCostOverTime(cacheKey);
        //   break;
        case "supplier-orders":
          await fetchSupplierOrders(cacheKey);
          break;
        default:
          break;
      }
    } catch (err: any) {
      console.error('Error fetching shipping analytics:', err);
      setError(err.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  // const fetchOverview = async (cacheKey?: string) => {
  //   const response = await supplyChainService.getShippingAnalyticsOverview(
  //     startDate,
  //     endDate,
  //     selectedCountry || undefined
  //   );
  //   if (response?.data) {
  //     if (response.data.error) {
  //       throw new Error(response.data.error);
  //     }
  //     setOverview(response.data);
  //     if (cacheKey) setCachedData(cacheKey, response.data); // Cache it
  //   }
  // };

  // const fetchByCountry = async (cacheKey?: string) => {
  //   const response = await supplyChainService.getShippingAnalyticsByCountry(
  //     startDate,
  //     endDate,
  //     groupBy,
  //     selectedCountry || undefined
  //   );
  //   if (response?.data) {
  //     setByCountryData(response.data);
  //     if (cacheKey) setCachedData(cacheKey, response.data); // Cache it
  //   }
  // };

  const fetchCostBands = async () => {
    const response = await supplyChainService.getShippingCostBands(
      startDate,
      endDate,
      selectedCountry || undefined,
      selectedOrigin || undefined
    );
    if (response?.data) {
      const formatted = response.data.map((item: any) => ({
        country: item.country,
        band: item.cost_band,
        shipment_count: item.shipment_count,
        avg_value: item.avg_cost_in_band,
        min_value: item.min_cost,
        max_value: item.max_cost
      }));
      setCostBands(formatted);
    }
  };

  const fetchWeightBands = async () => {
    const response = await supplyChainService.getShippingWeightBands(
      startDate,
      endDate,
      selectedCountry || undefined,
      selectedOrigin || undefined
    );
    if (response?.data) {
      const formatted = response.data.map((item: any) => ({
        country: item.country,
        band: item.weight_band,
        shipment_count: item.shipment_count,
        avg_value: item.avg_weight_in_band,
        min_value: item.min_weight,
        max_value: item.max_weight
      }));
      setWeightBands(formatted);
    }
  };

  const fetchCostPerItem = async () => {
    const response = await supplyChainService.getCostPerKgAnalysis(
      startDate,
      endDate,
      selectedCountry || undefined,
      selectedOrigin || undefined
    );
    if (response?.data) {
      setCostPerItem(response.data);
    }
  };

  // const fetchTrends = async (cacheKey?: string) => {
  //   const response = await supplyChainService.getShippingTrends(
  //     startDate,
  //     endDate,
  //     selectedCountry || undefined,
  //     metric
  //   );
  //   if (response?.data) {
  //     setTrends(response.data);
  //     if (cacheKey) setCachedData(cacheKey, response.data); // Cache it
  //   }
  // };

  //  Warehouse Analytics fetch functions
  const fetchWarehouseAnalytics = async (cacheKey?: string) => {
    const response = await supplyChainService.getWarehouseAnalytics(
      startDate,
      endDate,
      selectedOrigin || undefined
    );
    if (response?.data) {
      setWarehouseAnalytics(response.data);
      if (cacheKey) setCachedData(cacheKey, response.data); //  Cache it
    }
  };

  const fetchOrderComposition = async (cacheKey?: string) => {
    const response = await supplyChainService.getOrderComposition(
      startDate,
      endDate,
      selectedCountry || undefined
    );
    if (response?.data) {
      setOrderComposition(response.data);
      if (cacheKey) setCachedData(cacheKey, response.data); // Cache it
    }
  };

  // const fetchCnWarehouseByCountry = async (cacheKey?: string) => {
  //   const response = await supplyChainService.getCnWarehouseByCountry(
  //     startDate,
  //     endDate
  //   );
  //   if (response?.data) {
  //     setCnWarehouseByCountry(response.data);
  //     if (cacheKey) setCachedData(cacheKey, response.data); //  Cache it
  //   }
  // };

  const fetchAvgCostOverTime = async (cacheKey?: string) => {
    const response = await supplyChainService.getAvgCostByCountryOverTime(
      startDate,
      endDate,
      groupBy,
      selectedOrigin || undefined,
      selectedCountry || undefined
    );
    if (response?.data) {
      setAvgCostOverTime(response.data);
      if (cacheKey) setCachedData(cacheKey, response.data);
    }
  };

  const fetchSupplierOrders = async (cacheKey?: string) => {
    const response = await supplyChainService.getSupplierOrderHistory(
      startDate,
      endDate,
      itemFilter || undefined,
      statusFilter || undefined
    );
    if (response?.data && response.data.length > 0) {
      setSupplierOrders(response.data);
      setSupplierOrderSummary(response.summary);
      setSupplierFallbackInfo({ active: false, rangeLabel: "" });
      //  Cache both orders and summary
      if (cacheKey) setCachedData(cacheKey, { orders: response.data, summary: response.summary });
      return;
    }

    // If selected range has no records, show latest available history as fallback.
    const shouldFallbackToLatest = !itemFilter && !statusFilter;
    if (shouldFallbackToLatest) {
      const fallbackResponse = await supplyChainService.getSupplierOrderHistory(
        undefined,
        undefined,
        undefined,
        undefined
      );

      if (fallbackResponse?.data && fallbackResponse.data.length > 0) {
        const parsedDates = fallbackResponse.data
          .map((o: SupplierOrderData) => o.parsed_date)
          .filter((d): d is string => !!d)
          .sort();
        const earliest = parsedDates.length > 0 ? parsedDates[0].slice(0, 10) : "";
        const latest = parsedDates.length > 0 ? parsedDates[parsedDates.length - 1].slice(0, 10) : "";
        const rangeLabel = earliest && latest ? `${earliest} to ${latest}` : "latest available range";

        setSupplierOrders(fallbackResponse.data);
        setSupplierOrderSummary(fallbackResponse.summary);
        setSupplierFallbackInfo({
          active: true,
          rangeLabel
        });
        if (cacheKey) {
          setCachedData(cacheKey, {
            orders: fallbackResponse.data,
            summary: fallbackResponse.summary,
            fallback: { active: true, rangeLabel }
          });
        }
        return;
      }
    }

    setSupplierOrders([]);
    setSupplierOrderSummary(response?.summary || null);
    setSupplierFallbackInfo({ active: false, rangeLabel: "" });
    if (cacheKey) setCachedData(cacheKey, { orders: [], summary: response?.summary || null });
  };

  // Fetch drill-down data for warehouse row expansion
  const fetchWarehouseDrilldown = async (warehouse: string) => {
    // Check cache first
    const cacheKey = `warehouse_drilldown_${warehouse}_${startDate}_${endDate}_${groupBy}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setWarehouseDrilldownData(cachedData);
      return;
    }

    setLoadingDrilldown(true);
    try {
      const response = await supplyChainService.getAvgCostByCountryOverTime(
        startDate,
        endDate,
        groupBy,
        warehouse,
        undefined
      );
      if (response?.data) {
        setWarehouseDrilldownData(response.data);
        setCachedData(cacheKey, response.data);
      }
    } catch (err) {
      console.error("Error fetching warehouse drilldown:", err);
      setWarehouseDrilldownData([]);
    } finally {
      setLoadingDrilldown(false);
    }
  };

  // Handle warehouse row click for drill-down
  const handleWarehouseRowClick = (warehouse: string) => {
    if (expandedWarehouse === warehouse) {
      // Collapse if already expanded
      setExpandedWarehouse(null);
      setWarehouseDrilldownData([]);
    } else {
      // Expand and fetch data
      setExpandedWarehouse(warehouse);
      fetchWarehouseDrilldown(warehouse);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatNumber = (value: number, decimals: number = 2): string => {
    return value.toFixed(decimals);
  };

  // const renderOverview = () => {
  //   if (!overview) return <div className="p-4">Loading...</div>;

  //   return (
  //     <div className="p-8">
  //       <h2 className="text-xl font-bold text-gray-900 mb-6">Overview</h2>

  //       {/* Simple metric cards - Meta/Google style */}
  //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
  //         <div className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
  //           <div className="text-sm text-gray-500 mb-2">Total Shipments</div>
  //           <div className="text-3xl font-semibold text-gray-900">
  //             {overview.total_shipments?.toLocaleString() || 0}
  //           </div>
  //           <div className="text-xs text-gray-400 mt-2">
  //             {overview.first_shipment_date} - {overview.last_shipment_date}
  //           </div>
  //         </div>

  //         <div className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
  //           <div className="text-sm text-gray-500 mb-2">Total Cost</div>
  //           <div className="text-3xl font-semibold text-gray-900">
  //             {formatCurrency(overview.total_cost || 0)}
  //           </div>
  //           <div className="text-xs text-gray-400 mt-2">
  //             {formatCurrency(overview.avg_cost_per_shipment || 0)} avg
  //           </div>
  //         </div>

  //         <div className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
  //           <div className="text-sm text-gray-500 mb-2">Total Items</div>
  //           <div className="text-3xl font-semibold text-gray-900">
  //             {overview.total_items?.toLocaleString() || 0}
  //           </div>
  //           <div className="text-xs text-gray-400 mt-2">
  //             {formatNumber(overview.avg_items_per_shipment || 0, 1)} items avg
  //           </div>
  //         </div>

  //         <div className="border border-gray-200 rounded-lg p-6 hover:border-gray-300 transition-colors">
  //           <div className="text-sm text-gray-500 mb-2">Countries Served</div>
  //           <div className="text-3xl font-semibold text-gray-900">
  //             {overview.countries_served || 0}
  //           </div>
  //           <div className="text-xs text-gray-400 mt-2">
  //             destinations
  //           </div>
  //         </div>
  //       </div>

  //       {/* Additional metrics table*/}
  //       <div className="border border-gray-200 rounded-lg overflow-hidden">
  //         <table className="min-w-full divide-y divide-gray-200">
  //           <thead className="bg-gray-50">
  //             <tr>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Per Shipment</th>
  //             </tr>
  //           </thead>
  //           <tbody className="bg-white divide-y divide-gray-200">
  //             <tr>
  //               <td className="px-6 py-4 text-sm text-gray-900">Shipping Cost</td>
  //               <td className="px-6 py-4 text-sm font-medium text-gray-900">{formatCurrency(overview.total_cost || 0)}</td>
  //               <td className="px-6 py-4 text-sm text-gray-500">{formatCurrency(overview.avg_cost_per_shipment || 0)}</td>
  //             </tr>
  //             <tr>
  //               <td className="px-6 py-4 text-sm text-gray-900">Items Shipped</td>
  //               <td className="px-6 py-4 text-sm font-medium text-gray-900">{overview.total_items?.toLocaleString() || 0} items</td>
  //               <td className="px-6 py-4 text-sm text-gray-500">{formatNumber(overview.avg_items_per_shipment || 0, 2)} items</td>
  //             </tr>
  //             <tr>
  //               <td className="px-6 py-4 text-sm text-gray-900">Shipments</td>
  //               <td className="px-6 py-4 text-sm font-medium text-gray-900">{overview.total_shipments?.toLocaleString() || 0}</td>
  //               <td className="px-6 py-4 text-sm text-gray-500">{overview.countries_served || 0} countries</td>
  //             </tr>
  //           </tbody>
  //         </table>
  //       </div>
  //     </div>
  //   );
  // };

  // const renderByCountry = () => {
  //   // Group data by region for summary (costs only - shipment counts not split by region)
  //   const countryTotals = byCountryData.reduce((acc: any, item) => {
  //     if (!acc[item.country]) {
  //       acc[item.country] = {
  //         country: item.country,
  //         total_cost: 0,
  //         total_days: 0,
  //         min_daily_cost: Infinity,
  //         max_daily_cost: 0
  //       };
  //     }
  //     acc[item.country].total_cost += item.total_cost;
  //     acc[item.country].total_days += item.days_with_data;
  //     acc[item.country].min_daily_cost = Math.min(acc[item.country].min_daily_cost, item.min_daily_cost);
  //     acc[item.country].max_daily_cost = Math.max(acc[item.country].max_daily_cost, item.max_daily_cost);
  //     return acc;
  //   }, {});

  //   const sortedCountries = Object.values(countryTotals).sort(
  //     (a: any, b: any) => b.total_cost - a.total_cost
  //   );

  //   return (
  //     <div className="p-6">
  //       <h2 className="text-2xl font-bold mb-6">Last Mile Costs by Region</h2>

  //       {/* Explanatory Note */}
  //       <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
  //         <p className="text-sm text-blue-900">
  //           <strong>📊 Regional Cost Breakdown:</strong> This shows fulfillment costs split by <strong>USA</strong> and <strong>CN</strong> fulfillment regions.
  //           Shipment volumes are shown in the Overview tab (not split by region in source data).
  //         </p>
  //       </div>

  //       <div className="bg-white rounded-lg shadow overflow-hidden">
  //         <table className="min-w-full divide-y divide-gray-200">
  //           <thead className="bg-gray-50">
  //             <tr>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //                 Region
  //               </th>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //                 Total Cost
  //               </th>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //                 Avg Daily Cost
  //               </th>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //                 Min Daily Cost
  //               </th>
  //               <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
  //                 Max Daily Cost
  //               </th>
  //             </tr>
  //           </thead>
  //           <tbody className="bg-white divide-y divide-gray-200">
  //             {sortedCountries.map((country: any, idx: number) => (
  //               <tr key={idx} className="hover:bg-gray-50">
  //                 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
  //                   {country.country}
  //                 </td>
  //                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
  //                   {formatCurrency(country.total_cost)}
  //                 </td>
  //                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  //                   {formatCurrency(country.total_cost / country.total_days)}
  //                 </td>
  //                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  //                   {formatCurrency(country.min_daily_cost)}
  //                 </td>
  //                 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
  //                   {formatCurrency(country.max_daily_cost)}
  //                 </td>
  //               </tr>
  //             ))}
  //           </tbody>
  //         </table>
  //       </div>
  //     </div>
  //   );
  // };

  const renderBands = (bands: BandData[], title: string, unit: string) => {
    // Group by country
    const byCountry = bands.reduce((acc: any, item) => {
      if (!acc[item.country]) {
        acc[item.country] = [];
      }
      acc[item.country].push(item);
      return acc;
    }, {});

    const totalShipments = bands.reduce((sum, b) => sum + b.shipment_count, 0);

    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <div className="text-sm text-gray-500">
            {totalShipments.toLocaleString()} total shipments
          </div>
        </div>

        {/* Simple table view*/}
        <div className="space-y-6">
          {Object.entries(byCountry).slice(0, 20).map(([country, countryBands]: [string, any]) => {
            const totalCountryShipments = countryBands.reduce((sum: number, b: BandData) => sum + b.shipment_count, 0);

            return (
              <div key={country} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Country header */}
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-900">{country}</span>
                    <span className="text-sm text-gray-500">
                      {totalCountryShipments.toLocaleString()} shipments
                    </span>
                  </div>
                </div>

                {/* Table */}
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Band</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipments</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg {unit}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Range</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Share</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {countryBands.map((band: BandData, idx: number) => {
                      const percentage = (band.shipment_count / totalCountryShipments) * 100;

                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm text-gray-900">{band.band}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{band.shipment_count.toLocaleString()}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {unit === "$" ? formatCurrency(band.avg_value) : formatNumber(band.avg_value, 2)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {unit === "$" ? formatCurrency(band.min_value) : formatNumber(band.min_value, 1)} - {unit === "$" ? formatCurrency(band.max_value) : formatNumber(band.max_value, 1)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-gray-100 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-600 w-12 text-right">
                                {percentage.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {Object.keys(byCountry).length > 20 && (
          <div className="mt-6 text-center text-sm text-gray-500">
            Showing top 20 countries. Use filters to view specific regions.
          </div>
        )}
      </div>
    );
  };

  const renderCostPerItem = () => {
    const sortedData = [...costPerItem].sort((a, b) => b.avg_cost_per_item - a.avg_cost_per_item);

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Cost per Item Analysis</h2>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Shipments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Cost/Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Min Cost/Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Cost/Item
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.country}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.shipment_count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.total_items.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                    {formatCurrency(item.avg_cost_per_item)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(item.min_cost_per_item)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(item.max_cost_per_item)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // const renderTrends = () => {
  //   if (trends.length === 0) {
  //     return <div className="p-6">No trend data available</div>;
  //   }

  //   // Format data for Recharts
  //   const chartData = trends.map(trend => ({
  //     date: new Date(trend.period).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  //     value: trend.value
  //   }));

  //   const avgValue = trends.reduce((sum, t) => sum + t.value, 0) / trends.length;
  //   const latestValue = trends[trends.length - 1]?.value || 0;

  //   // Custom tooltip
  //   const CustomTooltip = ({ active, payload }: any) => {
  //     if (active && payload && payload.length) {
  //       const value = payload[0].value;
  //       const percentDiffNum = ((value - avgValue) / avgValue * 100);
  //       const percentDiff = percentDiffNum.toFixed(1);
  //       const isAboveAvg = value > avgValue;

  //       // Color logic based on metric type 
  //       // For costs: above avg = bad (red), below avg = good (green)
  //       // For counts/revenue: above avg = good (green), below avg = bad (red)
  //       const isGood = metric === 'cost' ? !isAboveAvg : isAboveAvg;

  //       return (
  //         <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-xl min-w-[200px]">
  //           <p className="text-xs font-medium text-gray-500 mb-2">{payload[0].payload.date}</p>

  //           <div className="mb-3">
  //             <p className="text-xs text-gray-600 mb-1">
  //               {metric === 'cost' ? 'Shipping Cost' :
  //                 metric === 'count' ? 'Shipments' :
  //                   'Value'}
  //             </p>
  //             <p className="text-lg font-bold text-gray-900">
  //               {metric === 'cost' ? formatCurrency(value) :
  //                 metric === 'count' ? value.toFixed(0) :
  //                   formatNumber(value, 2)}
  //             </p>
  //           </div>

  //           <div className="pt-3 border-t border-gray-200">
  //             <div className="flex justify-between items-center mb-1">
  //               <p className="text-xs text-gray-600">vs Average</p>
  //               <p className={`text-xs font-semibold ${isGood ? 'text-green-600' : 'text-red-600'}`}>
  //                 {percentDiffNum > 0 ? '+' : ''}{percentDiff}%
  //               </p>
  //             </div>
  //             <p className="text-xs text-gray-500">
  //               Avg: {metric === 'cost' ? formatCurrency(avgValue) :
  //                 metric === 'count' ? avgValue.toFixed(0) :
  //                   formatNumber(avgValue, 2)}
  //             </p>
  //           </div>
  //         </div>
  //       );
  //     }
  //     return null;
  //   };

  //   return (
  //     <div className="p-8">
  //       <h2 className="text-xl font-bold text-gray-900 mb-6">Shipping Trends - {metric}</h2>

  //       {/* Stats Summary */}
  //       <div className="grid grid-cols-2 gap-4 mb-8">
  //         <div className="border border-gray-200 rounded-lg p-4">
  //           <div className="text-sm text-gray-500 mb-1">Latest</div>
  //           <div className="text-2xl font-semibold text-gray-900">
  //             {metric === 'cost' ? formatCurrency(latestValue) :
  //               metric === 'count' ? latestValue.toFixed(0) :
  //                 formatNumber(latestValue, 2)}
  //           </div>
  //         </div>
  //         <div className="border border-gray-200 rounded-lg p-4">
  //           <div className="text-sm text-gray-500 mb-1">Average</div>
  //           <div className="text-2xl font-semibold text-gray-900">
  //             {metric === 'cost' ? formatCurrency(avgValue) :
  //               metric === 'count' ? avgValue.toFixed(0) :
  //                 formatNumber(avgValue, 2)}
  //           </div>
  //         </div>
  //       </div>

  //       {/* Line Chart */}
  //       <div className="border border-gray-200 rounded-lg p-6" style={{ height: '400px' }}>
  //         <ResponsiveContainer width="100%" height="100%">
  //           <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
  //             <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
  //             <XAxis
  //               dataKey="date"
  //               stroke="#9ca3af"
  //               tick={{ fontSize: 12 }}
  //               interval="preserveStartEnd"
  //             />
  //             <YAxis
  //               stroke="#9ca3af"
  //               tick={{ fontSize: 12 }}
  //               tickFormatter={(value) =>
  //                 metric === 'cost' ? `$${value.toFixed(0)}` :
  //                   metric === 'count' ? value.toFixed(0) :
  //                     value.toFixed(1)
  //               }
  //             />
  //             <Tooltip content={<CustomTooltip />} />
  //             <Line
  //               type="monotone"
  //               dataKey="value"
  //               stroke="#3b82f6"
  //               strokeWidth={2}
  //               dot={{ fill: '#3b82f6', r: 3 }}
  //               activeDot={{ r: 5 }}
  //             />
  //           </LineChart>
  //         </ResponsiveContainer>
  //       </div>
  //     </div>
  //   );
  // };

  // Warehouse Analytics render functions
  const renderWarehouseAnalytics = () => {
    // total_volume is no longer available (see WarehouseData), so sort by cost instead
    const sortedWarehouses = [...warehouseAnalytics].sort((a, b) => b.total_cost - a.total_cost);

    // Origin options for the dropdown
    const originOptions = [
      { value: "", label: "All Origins" },
      { value: "Huboo", label: "Huboo (UK)" },
      { value: "OpenBorder", label: "OpenBorder (AU)" },
      { value: "Huboo_UK", label: "Huboo UK" },
      { value: "OpenBorder_AU", label: "OpenBorder AU" },
      { value: "EcommOps", label: "EcommOps (China)" },
      { value: "YunExpress", label: "YunExpress" }
    ];

    // Process drill-down data for expanded warehouse
    const getDrilldownByCountry = () => {
      if (!warehouseDrilldownData.length) return [];
      
      // Get unique countries
      const countries = Array.from(new Set(warehouseDrilldownData.map(d => d.country)));
      
      // Get unique periods sorted
      const periods = Array.from(new Set(warehouseDrilldownData.map(d => d.period))).sort();
      
      // Aggregate data by country
      return countries.map(country => {
        const countryData = warehouseDrilldownData.filter(d => d.country === country);
        const totalOrders = countryData.reduce((sum, d) => sum + d.order_count, 0);
        const totalCost = countryData.reduce((sum, d) => sum + d.total_cost, 0);
        const totalCogs = countryData.reduce((sum, d) => sum + d.total_cogs, 0);
        const avgShipCost = totalOrders > 0 ? totalCost / totalOrders : 0;
        const avgCogs = totalOrders > 0 ? totalCogs / totalOrders : 0;
        
        // Get cost per period for time-series columns
        const periodCosts: { [key: string]: number } = {};
        periods.forEach(period => {
          const periodData = countryData.find(d => d.period === period);
          periodCosts[period] = periodData?.avg_cost || 0;
        });
        
        return {
          country,
          totalOrders,
          avgShipCost,
          avgCogs,
          periodCosts,
          periods
        };
      }).sort((a, b) => b.totalOrders - a.totalOrders);
    };

    const drilldownCountries = getDrilldownByCountry();
    const drilldownPeriods = drilldownCountries.length > 0 ? drilldownCountries[0].periods : [];

    // Format period for display
    const formatPeriod = (period: string) => {
      const date = new Date(period);
      if (groupBy === 'week') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
      return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    };

    // Process avgCostOverTime data for chart (moved from Cost Over Time tab)
    const periodData = avgCostOverTime.reduce((acc: any, item) => {
      if (!acc[item.period]) {
        acc[item.period] = {
          period: item.period,
          countries: {},
          total_orders: 0,
          total_cost: 0,
          total_cogs: 0
        };
      }
      acc[item.period].countries[item.country] = item.avg_cost;
      acc[item.period].total_orders += item.order_count;
      acc[item.period].total_cost += item.total_cost;
      acc[item.period].total_cogs += item.total_cogs;
      return acc;
    }, {});

    const chartData = Object.values(periodData)
      .sort((a: any, b: any) => a.period.localeCompare(b.period))
      .map((p: any) => ({
        ...p,
        avg_cost: p.total_orders > 0 ? p.total_cost / p.total_orders : 0,
        avg_cogs: p.total_orders > 0 ? p.total_cogs / p.total_orders : 0
      }));

    // Get unique countries with their totals for summary
    const countryTotals = avgCostOverTime.reduce((acc: any, item) => {
      if (!acc[item.country]) {
        acc[item.country] = {
          country: item.country,
          total_orders: 0,
          total_cost: 0,
          total_cogs: 0
        };
      }
      acc[item.country].total_orders += item.order_count;
      acc[item.country].total_cost += item.total_cost;
      acc[item.country].total_cogs += item.total_cogs;
      return acc;
    }, {});

    return (
      <div className="p-8">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Warehouse Analytics</h2>
            <p className="text-sm text-gray-500">
              Shipping cost analysis by warehouse with country breakdown
            </p>
          </div>

          {/* Origin Filter Dropdown */}
          <div className="w-56">
            <CustomDropdown
              value={originOptions.find(opt => opt.value === selectedOrigin)?.label || ""}
              onChange={(val) => {
                const selected = originOptions.find(opt => opt.label === val);
                if (selected) setSelectedOrigin(selected.value);
              }}
              options={originOptions.map(opt => opt.label)}
              label="Origin:"
              isSearchVisible={false}
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 mt-4">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Unique Countries</div>
            <div className="text-2xl font-semibold text-gray-900">
              {Object.keys(countryTotals).length}
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Total Orders</div>
            <div className="text-2xl font-semibold text-gray-900">
              {avgCostOverTime.reduce((sum, d) => sum + d.order_count, 0).toLocaleString()}
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Total Cost</div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatCurrency(avgCostOverTime.reduce((sum, d) => sum + d.total_cost, 0))}
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-500 mb-1">Total COGS</div>
            <div className="text-2xl font-semibold text-gray-900">
              {formatCurrency(avgCostOverTime.reduce((sum, d) => sum + d.total_cogs, 0))}
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
            <div className="text-sm text-blue-600 mb-1 font-medium">Total (Ship + COGS)</div>
            <div className="text-2xl font-bold text-blue-900">
              {formatCurrency(avgCostOverTime.reduce((sum, d) => sum + d.total_cost + d.total_cogs, 0))}
            </div>
          </div>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Overall Average Cost Per Order Trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="period"
                    tickFormatter={formatPeriod}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={(v) => `$${v.toFixed(0)}`}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) => [formatCurrency(value), name]}
                    labelFormatter={formatPeriod}
                  />
                  <Legend verticalAlign="top" height={36} />
                  <Line
                    type="monotone"
                    dataKey="avg_cost"
                    name="Avg Ship Cost"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: '#10b981', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="avg_cogs"
                    name="Avg COGS"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Warehouse Table Header */}
        <h3 className="text-sm font-medium text-gray-900 mb-3">By Warehouse Breakdown</h3>

        <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">Warehouse</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Orders</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Volume</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Avg Weight</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Avg Cost</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Total Cost</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedWarehouses.map((warehouse, idx) => (
                  <React.Fragment key={idx}>
                    {/* Main warehouse row */}
                    <tr 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors cursor-pointer`}
                      onClick={() => handleWarehouseRowClick(warehouse.warehouse)}
                    >
                      <td className="px-4 py-2 text-sm font-bold text-gray-900">
                        <div className="flex items-center gap-2">
                          <svg 
                            className={`w-4 h-4 text-gray-500 transition-transform ${expandedWarehouse === warehouse.warehouse ? 'rotate-90' : ''}`}
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          {warehouse.warehouse}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{warehouse.order_count.toLocaleString()}</td>
                      <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{warehouse.total_volume != null ? warehouse.total_volume.toLocaleString() : '—'}</td>
                      <td className="px-4 py-2 text-sm text-right text-gray-600">{warehouse.avg_weight_kg != null ? `${warehouse.avg_weight_kg.toFixed(2)} kg` : '—'}</td>
                      <td className="px-4 py-2 text-sm text-right font-bold text-green-700">{formatCurrency(warehouse.avg_cost)}</td>
                      <td className="px-4 py-2 text-sm text-right font-bold text-gray-900">{formatCurrency(warehouse.total_cost)}</td>
                    </tr>
                    
                    {/* Expanded drill-down row */}
                    {expandedWarehouse === warehouse.warehouse && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <div className="bg-gray-50 border-t border-b border-gray-200">
                            {loadingDrilldown ? (
                              <div className="p-6 text-center">
                                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <p className="mt-2 text-sm text-gray-600">Loading country breakdown...</p>
                              </div>
                            ) : drilldownCountries.length === 0 ? (
                              <div className="p-6 text-center text-gray-500 text-sm">
                                No country breakdown data available for this warehouse.
                              </div>
                            ) : (
                              <div className="p-4">
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Country</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Orders</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Ship/Order</th>
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">COGS/Order</th>
                                        {drilldownPeriods.slice(-6).map(period => (
                                          <th key={period} className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">
                                            {formatPeriod(period)}
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                      {drilldownCountries.map((row, rowIdx) => (
                                        <tr key={rowIdx} className="hover:bg-gray-50">
                                          <td className="px-3 py-2 font-medium text-gray-900">{row.country}</td>
                                          <td className="px-3 py-2 text-right text-gray-700">{row.totalOrders.toLocaleString()}</td>
                                          <td className="px-3 py-2 text-right font-semibold text-green-700">${row.avgShipCost.toFixed(2)}</td>
                                          <td className="px-3 py-2 text-right font-semibold text-purple-700">${row.avgCogs.toFixed(2)}</td>
                                          {drilldownPeriods.slice(-6).map(period => (
                                            <td key={period} className="px-3 py-2 text-right text-gray-600">
                                              {row.periodCosts[period] > 0 ? `$${row.periodCosts[period].toFixed(2)}` : '-'}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderOrderComposition = () => {
    if (orderComposition.length === 0) {
      return (
        <div className="p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Order Composition</h2>
          <div className="text-gray-600 text-sm">No order composition data available for the selected date range.</div>
        </div>
      );
    }

    const sortedCompositions = [...orderComposition].sort((a, b) => b.order_count - a.order_count);

    return (
      <div className="p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Order Composition Breakdown</h2>

        <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">Composition</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Orders</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">%</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Avg Units</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Avg Cost</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Total Units</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedCompositions.map((comp, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                    <td className="px-4 py-2 text-sm font-bold text-gray-900">{comp.composition}</td>
                    <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{comp.order_count.toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-right font-bold text-blue-700">{comp.percentage.toFixed(1)}%</td>
                    <td className="px-4 py-2 text-sm text-right text-gray-600">{comp.avg_units_per_order.toFixed(1)}</td>
                    <td className="px-4 py-2 text-sm text-right font-semibold text-green-700">${comp.avg_cost.toFixed(2)}</td>
                    <td className="px-4 py-2 text-sm text-right font-bold text-gray-900">{comp.total_units_sold.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // const renderCnWarehouseByCountry = () => {
  //   const sortedCountries = [...cnWarehouseByCountry].sort((a, b) => b.total_volume - a.total_volume);

  //   return (
  //     <div className="p-8">
  //       <h2 className="text-xl font-bold text-gray-900 mb-6">CN Warehouse by Destination Country</h2>

  //       <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm">
  //         <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
  //           <table className="min-w-full divide-y divide-gray-300">
  //             <thead className="bg-gray-800 sticky top-0 z-10">
  //               <tr>
  //                 <th className="px-4 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">Country</th>
  //                 <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Orders</th>
  //                 <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Volume</th>
  //                 <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Avg Weight</th>
  //                 <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Avg Cost</th>
  //                 <th className="px-4 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Total Cost</th>
  //               </tr>
  //             </thead>
  //             <tbody className="bg-white divide-y divide-gray-200">
  //               {sortedCountries.slice(0, 50).map((country, idx) => (
  //                 <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
  //                   <td className="px-4 py-2 text-sm font-bold text-gray-900">{country.destination_country}</td>
  //                   <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{country.order_count.toLocaleString()}</td>
  //                   <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{country.total_volume.toLocaleString()}</td>
  //                   <td className="px-4 py-2 text-sm text-right text-gray-600">{country.avg_weight_kg.toFixed(2)} kg</td>
  //                   <td className="px-4 py-2 text-sm text-right font-bold text-green-700">{formatCurrency(country.avg_cost)}</td>
  //                   <td className="px-4 py-2 text-sm text-right font-bold text-gray-900">{formatCurrency(country.total_cost)}</td>
  //                 </tr>
  //               ))}
  //             </tbody>
  //           </table>
  //         </div>
  //       </div>

  //       {sortedCountries.length > 50 && (
  //         <div className="mt-3 text-xs text-gray-600 text-center font-medium">
  //           Showing top 50 countries out of {sortedCountries.length} total
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  // renderCostOverTime moved to renderWarehouseAnalytics - commented out

  const renderSupplierOrders = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Delivered': return 'text-green-600 bg-green-50';
        case 'In Transit': return 'text-blue-600 bg-blue-50';
        case 'Ordered': return 'text-yellow-600 bg-yellow-50';
        default: return 'text-gray-600 bg-gray-50';
      }
    };

    return (
      <div className="p-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Supplier Order History</h2>

        {/* Summary Cards */}
        {supplierOrderSummary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Total Orders</div>
              <div className="text-2xl font-semibold text-gray-900">
                {supplierOrderSummary.total_orders.toLocaleString()}
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Total Amount</div>
              <div className="text-2xl font-semibold text-gray-900">
                {formatCurrency(supplierOrderSummary.total_amount)}
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Total Quantity</div>
              <div className="text-2xl font-semibold text-gray-900">
                {supplierOrderSummary.total_quantity.toLocaleString()} sets
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">In Transit</div>
              <div className="text-2xl font-semibold text-blue-600">
                {supplierOrderSummary.by_status.in_transit}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {supplierOrderSummary.by_status.delivered} delivered, {supplierOrderSummary.by_status.ordered} ordered
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={itemFilter}
              onChange={(e) => setItemFilter(e.target.value)}
              placeholder="Filter by item name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
          <div>
            <div className="min-w-[170px]">
              <CustomDropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={["Ordered", "In Transit", "Delivered"]}
                placeholder="All Status"
                label="Status"
                hideLabel={true}
                isSearchVisible={false}
              />
            </div>
          </div>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none transition-colors text-sm font-medium"
          >
            Apply Filters
          </button>
        </div>

        {supplierFallbackInfo.active && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No supplier orders found in the selected date range. Showing latest available orders ({supplierFallbackInfo.rangeLabel}).
          </div>
        )}

        {/* Orders Table */}
        <div className="border border-gray-300 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto max-h-[calc(100vh-350px)] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-800 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">Order #</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">Date</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">Items</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Quantity</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Amount</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wider">In Transit</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-white uppercase tracking-wider">Stock</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-white uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {supplierOrders.map((order, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}>
                    <td className="px-3 py-2 text-xs font-semibold text-gray-900">{order.order_number}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{order.order_date}</td>
                    <td className="px-3 py-2 text-xs">
                      {order.item_count > 1 ? (
                        <div>
                          <div className="font-semibold text-blue-700 mb-0.5 text-[11px]">
                            {order.item_count} Products:
                          </div>
                          <ul className="space-y-0.5 text-[11px]">
                            {order.items.split(',').slice(0, 4).map((item, idx) => (
                              <li key={idx} className="text-gray-700 leading-tight">
                                • {item.trim().replace('Cheatsheet-', '').replace('\n', ' ')}
                              </li>
                            ))}
                            {order.items.split(',').length > 4 && (
                              <li className="text-gray-500 italic">+{order.items.split(',').length - 4} more</li>
                            )}
                          </ul>
                        </div>
                      ) : (
                        <div className="text-gray-800 font-medium">
                          {order.items.replace('Cheatsheet-', '').replace('\n', ' ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-semibold text-gray-900">{order.quantity.toLocaleString()}</td>
                    <td className="px-3 py-2 text-xs text-right font-bold text-gray-900">{formatCurrency(order.order_amount)}</td>
                    <td className="px-3 py-2 text-[11px] text-gray-600">
                      {order.in_transit.total > 0 ? (
                        <div className="space-y-0.5">
                          {['CN', 'US', 'CA', 'UK', 'AU'].map(wh => {
                            const shipments = order.in_transit[wh as keyof typeof order.in_transit];
                            if (Array.isArray(shipments) && shipments.length > 0) {
                              return (
                                <div key={wh} className="text-[10px]">
                                  <span className="font-semibold text-gray-800">{wh}:</span>{' '}
                                  <span className="font-medium">{shipments.reduce((sum, s) => sum + s.quantity, 0).toLocaleString()}</span>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-right font-semibold text-gray-900">
                      {order.current_stock > 0 ? order.current_stock.toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded uppercase ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {supplierOrders.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No supplier orders found for the selected filters or date range.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full bg-white">
      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>

          {/* <CustomDropdown
            value={selectedCountry}
            onChange={setSelectedCountry}
            options={countries}
            placeholder="All Regions"
            label="Region"
          /> */}

          {(activeTab === "by-country" || activeTab === "by-warehouse") && <CustomDropdown
            value={groupBy}
            onChange={setGroupBy}
            options={["week", "month"]}
            label="Group By"
            isSearchVisible={false}
          />}

          {/* {activeTab === "trends" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metric
              </label>
              <select
                value={metric}
                onChange={(e) => setMetric(e.target.value)}
                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="cost">Cost</option>
                <option value="weight">Weight</option>
                <option value="volume">Volume</option>
                <option value="count">Shipment Count</option>
              </select>
            </div>
          )} */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 invisible">
              Action
            </label>
            <button
              onClick={fetchData}
              className="w-full h-10 px-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none transition-colors text-sm font-medium"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-8">
        <nav className="flex space-x-6 overflow-x-auto" aria-label="Tabs">
          {[
            // { id: "overview", label: "Overview" },
            // { id: "by-country", label: "By Region" },
            // { id: "trends", label: "Trends" },
            { id: "by-warehouse", label: "By Warehouse" },
            { id: "order-composition", label: "Order Composition" },
            // { id: "cn-warehouse", label: "CN Warehouse" },
            // { id: "cost-over-time", label: "Cost Over Time" }, // Moved to By Warehouse tab
            { id: "supplier-orders", label: "Order History" }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === tab.id
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="overflow-auto" style={{ height: 'calc(100vh - 16rem)' }}>
        {loading && (
          <div className="p-6 space-y-6">
            {/* Summary Cards Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                  <div className="h-3 bg-gray-200 rounded w-24 animate-pulse mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-20 animate-pulse mb-1"></div>
                  <div className="h-2 bg-gray-100 rounded w-16 animate-pulse"></div>
                </div>
              ))}
            </div>

            {/* Chart/Table Skeleton */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              {/* Chart Header */}
              <div className="p-4 border-b border-gray-200">
                <div className="h-5 bg-gray-200 rounded w-48 animate-pulse mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-64 animate-pulse"></div>
              </div>

              {/* Table Header */}
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <div className="grid grid-cols-6 gap-4">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-gray-200">
                {[...Array(6)].map((_, rowIndex) => (
                  <div key={rowIndex} className="px-6 py-4">
                    <div className="grid grid-cols-6 gap-4 items-center">
                      {/* First Column */}
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                        <div className="h-3 bg-gray-100 rounded w-14 animate-pulse"></div>
                      </div>
                      {/* Other Columns */}
                      {[...Array(5)].map((_, colIndex) => (
                        <div key={colIndex} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Loading Indicator */}
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                <span className="ml-3 text-sm">Loading analytics...</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-800">Error: {error}</div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* {activeTab === "overview" && renderOverview()} */}
            {/* {activeTab === "by-country" && renderByCountry()} */}
            {activeTab === "cost-bands" && renderBands(costBands, "Cost Bands", "$")}
            {activeTab === "weight-bands" && renderBands(weightBands, "Item Bands", "items")}
            {activeTab === "cost-per-item" && renderCostPerItem()}
            {/* {activeTab === "trends" && renderTrends()} */}
            {activeTab === "by-warehouse" && renderWarehouseAnalytics()}
            {activeTab === "order-composition" && renderOrderComposition()}
            {/* {activeTab === "cn-warehouse" && renderCnWarehouseByCountry()}*/}
            {/* {activeTab === "cost-over-time" && renderCostOverTime()} // Moved to By Warehouse tab */}
            {activeTab === "supplier-orders" && renderSupplierOrders()}
          </>
        )}
      </div>
    </div>
  );
};

export default ShippingAnalytics;

